import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfigProvider } from "antd";
import Seo from "../common/Seo.js";
import Login from "../home/Login.js";
import Register from "../home/Register.js";
import forest from "../../assets/landing/forest-tile-v5.webp";
import plains from "../../assets/landing/plains-tile-v3.webp";
import mountains from "../../assets/landing/mountain-tile.webp";
import water from "../../assets/landing/fresh-water-tile-v2.webp";
import woodcutter from "../../customIcons/svgs/sites/Woodcutter.svg";
import workshop from "../../customIcons/svgs/realestates/Workshop.svg";
import store from "../../customIcons/svgs/sites/Supermarket.svg";
import "./PublicSite.css";

const businesses = [
  { name: "Harvest", text: "Gather wood, ore, crops, and fish to supply the economy.", tile: 7, icon: woodcutter, location: "Woodcutter" },
  { name: "Refine", text: "Turn raw materials into goods through connected production chains.", tile: 8, icon: workshop, location: "Workshop" },
  { name: "Sell", text: "Choose a location where demand is high and bring your goods to market.", tile: 12, icon: store, location: "Supermarket" },
];

const authTheme = {
  token: { controlHeight: 40 },
};

const tiles = [
  [200, 88, mountains], [320, 88, mountains], [440, 88, plains],
  [140, 192, forest], [260, 192, forest], [380, 192, plains], [500, 192, water],
  [80, 296, forest], [200, 296, plains], [320, 296, plains], [440, 296, water],
  [140, 400, forest], [260, 400, plains], [380, 400, water],
];

const hexPoints = (x, y) => `${x},${y - 68} ${x + 58},${y - 34} ${x + 58},${y + 34} ${x},${y + 68} ${x - 58},${y + 34} ${x - 58},${y - 34}`;

function WorldIllustration({ selected }) {
  return (
    <svg className="landing-map" viewBox="0 0 580 500" role="img" aria-label={`Illustrated game terrain. Selected business: ${businesses[selected].name}.`}>
      <defs>
        {tiles.map(([x, y], index) => <clipPath key={index} id={`landing-hex-${index}`}><polygon points={hexPoints(x, y)} /></clipPath>)}
      </defs>
      {tiles.map(([x, y, art], index) => (
        <g key={index} className={businesses[selected].tile === index ? "landing-hex is-selected" : "landing-hex"}>
          <image href={art} x={x - 68} y={y - 68} width="136" height="136" clipPath={`url(#landing-hex-${index})`} />
          <polygon points={hexPoints(x, y)} />
        </g>
      ))}
      <path className="landing-trade-route" d="M80 296 Q140 350 200 296 Q260 330 260 400" />
      {businesses.map((business, businessIndex) => {
        const [x, y] = tiles[business.tile];
        return (
          <g key={business.name} className={`landing-map-marker${selected === businessIndex ? " is-selected" : ""}`} transform={`translate(${x},${y})`}>
            <circle r="31" />
            <image href={business.icon} x="-23" y="-23" width="46" height="46" />
            <rect x="-49" y="35" width="98" height="25" rx="6" />
            <text textAnchor="middle" y="52">{business.location}</text>
          </g>
        );
      })}
    </svg>
  );
}

const structuredData = {
  "@context": "https://schema.org", "@type": "VideoGame", name: "Age of Companies",
  url: "https://www.ageofcompanies.com", description: "A persistent multiplayer economy and company management game played in the browser.",
  applicationCategory: "Game", gamePlatform: "Web browser", genre: ["Economy simulation", "Business simulation", "Massively multiplayer online game"], inLanguage: "en",
};

export default function LandingPage() {
  const [selectedBusiness, setSelectedBusiness] = useState(0);
  const [authCredentials, setAuthCredentials] = useState({});
  const [searchParams] = useSearchParams();
  const isRegistering = searchParams.get("auth") === "register";
  const business = businesses[selectedBusiness];

  return (
    <>
      <Seo title="Age of Companies | Persistent Multiplayer Economy Game" description="Build production chains, trade with other players, and grow a company in a persistent browser-based economy simulation." path="/" structuredData={structuredData} />
      <main id="main-content">
        <section className="landing-hero" aria-labelledby="landing-title">
          <div className="landing-hero-inner">
            <div className="landing-pitch">
              <h1 id="landing-title">Find your niche.<br /><em>Grow your company.</em></h1>
              <p>Turn raw resources into a thriving business. Hire workers, set up production chains, and trade with other players in a dynamic, persistent world.</p>
              <p className="landing-browser-note">Play in your browser—no download needed.</p>
            </div>
            <div className="landing-auth">
              <div className="landing-auth-heading">
                <h2>{isRegistering ? "Register" : "Sign in"}</h2>
                <span className="landing-free-to-play">Free to play</span>
              </div>
              <ConfigProvider theme={authTheme}>
                {isRegistering
                  ? <Register embedded initialCredentials={authCredentials} onCredentialsChange={setAuthCredentials} />
                  : <Login embedded initialCredentials={authCredentials} onCredentialsChange={setAuthCredentials} />}
              </ConfigProvider>
            </div>
            <div className="landing-world">
              <WorldIllustration selected={selectedBusiness} />
              <div className="landing-business-picker" role="group" aria-label="Explore example businesses">
                {businesses.map((item, index) => <button key={item.name} type="button" aria-pressed={selectedBusiness === index} onClick={() => setSelectedBusiness(index)}><span>{index + 1}</span>{item.name}</button>)}
              </div>
              <div className="landing-business-detail" aria-live="polite" aria-atomic="true">
                <strong>{business.name}</strong>
                <p>{business.text}</p>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
}
